import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import {
    offlineStore,
    type OfflineSyncFailure,
} from "../../utils/offlineStore";
import { useInspectionAttributes, useTask } from "../../hooks/query";
import { useNotification } from "../../hooks";
import { Box, Button, Typography } from "@mui/joy";
import { RefreshCw, X } from "lucide-react";
import { useCreateProjectHandler } from "../Project/components/NewProject/hooks";
import { expandMeasurementsForSync } from "../InitialInspection/utils/measurements";

export function SyncManager() {
    const isOnline = useNetworkStatus();
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastFailures, setLastFailures] = useState<OfflineSyncFailure[]>([]);
    const syncInProgressRef = useRef(false);
    const hasAttemptedSyncRef = useRef(false);
    const { createProjectEventHandler } = useCreateProjectHandler();
    const { useCreate } = useInspectionAttributes();
    const { useListByProjectId, useUploadAsset } = useTask();
    const createAttributeSvc = useCreate();
    const listTasksSvc = useListByProjectId();
    const uploadAssetSvc = useUploadAsset();
    const { showSuccess, showError } = useNotification();

    const createProjectRef = useRef(createProjectEventHandler);
    const createAttributeRef = useRef(createAttributeSvc.mutateAsync);
    const listTasksRef = useRef(listTasksSvc.mutateAsync);
    const uploadAssetRef = useRef(uploadAssetSvc.mutateAsync);
    const showSuccessRef = useRef(showSuccess);
    const showErrorRef = useRef(showError);
    createProjectRef.current = createProjectEventHandler;
    createAttributeRef.current = createAttributeSvc.mutateAsync;
    listTasksRef.current = listTasksSvc.mutateAsync;
    uploadAssetRef.current = uploadAssetSvc.mutateAsync;
    showSuccessRef.current = showSuccess;
    showErrorRef.current = showError;

    const dismissFailures = useCallback(async () => {
        await offlineStore.saveSyncFailures([]);
        setLastFailures([]);
    }, []);

    const syncOfflineQueue = useCallback(async (opts?: { manual?: boolean }) => {
        if (syncInProgressRef.current) return;
        if (typeof navigator !== "undefined" && !navigator.onLine) {
            console.warn("[SyncManager] Skipping sync — navigator.onLine is false");
            return;
        }

        try {
            const projects = await offlineStore.getProjects();
            const inspections = await offlineStore.getInspections();
            const offlinePhotos = await offlineStore.getOfflinePhotos();

            if (
                projects.length === 0 &&
                inspections.length === 0 &&
                offlinePhotos.length === 0
            ) {
                // Nothing left to sync — clear any leftover banner from earlier attempts.
                await offlineStore.saveSyncFailures([]);
                setLastFailures([]);
                return;
            }

            console.info("[SyncManager] Starting sync", {
                projects: projects.length,
                inspections: inspections.length,
                photos: offlinePhotos.length,
            });

            syncInProgressRef.current = true;
            setIsSyncing(true);
            const failures: OfflineSyncFailure[] = [];
            const deferred: OfflineSyncFailure[] = [];

            const idMap = new Map<string, string>();
            const pendingProjectIds = new Set(projects.map((p) => p.offlineId));

            for (const proj of projects) {
                try {
                    const res = await createProjectRef.current(proj.payload, {
                        forceOnline: true,
                    });
                    if (
                        res &&
                        res.projectId &&
                        !String(res.projectId).startsWith("offline-")
                    ) {
                        idMap.set(proj.offlineId, res.projectId);
                        await offlineStore.saveProjectMapping(
                            proj.offlineId,
                            res.projectId,
                        );
                        await offlineStore.removeProject(proj.offlineId);
                        pendingProjectIds.delete(proj.offlineId);
                        console.info(
                            "[SyncManager] Project synced",
                            proj.offlineId,
                            "→",
                            res.projectId,
                        );
                    } else {
                        const message =
                            "Create did not return a server project id" +
                            (res?.projectId
                                ? ` (got ${res.projectId})`
                                : " (got null)");
                        console.warn("[SyncManager]", message, proj.offlineId);
                        failures.push({
                            kind: "project",
                            id: proj.offlineId,
                            message,
                            at: Date.now(),
                        });
                    }
                } catch (e: any) {
                    console.error("[SyncManager] Failed to sync project", e);
                    failures.push({
                        kind: "project",
                        id: proj.offlineId,
                        message: e?.message || "Project sync failed",
                        at: Date.now(),
                    });
                }
            }

            for (const insp of inspections) {
                try {
                    const mappedId = await offlineStore.getProjectMapping(
                        insp.offlineId,
                    );
                    const realProjectId =
                        idMap.get(insp.offlineId) ||
                        mappedId ||
                        insp.offlineId;

                    if (String(realProjectId).startsWith("offline-")) {
                        if (pendingProjectIds.has(insp.offlineId)) {
                            deferred.push({
                                kind: "inspection",
                                id: insp.offlineId,
                                message: "Waiting for project sync",
                                at: Date.now(),
                            });
                        } else {
                            const message =
                                "Inspection has no server project mapping (project missing from queue)";
                            console.warn("[SyncManager]", message, insp.offlineId);
                            failures.push({
                                kind: "inspection",
                                id: insp.offlineId,
                                message,
                                at: Date.now(),
                            });
                        }
                        continue;
                    }

                    const expandedData = expandMeasurementsForSync(
                        insp.payload,
                    );

                    for (const key in expandedData) {
                        let value = expandedData[key];

                        if (
                            value &&
                            typeof value === "object" &&
                            !Array.isArray(value) &&
                            (value as { __measurement?: boolean }).__measurement
                        ) {
                            const measurementValue = value as {
                                Value: string;
                            };
                            await createAttributeRef.current({
                                ProjectId: realProjectId,
                                AttributeName: key,
                                Value: measurementValue.Value,
                            });
                            continue;
                        }

                        if (
                            Array.isArray(value) ||
                            (typeof value === "object" && value !== null)
                        ) {
                            value = JSON.stringify(value);
                        }

                        if (value === undefined || value === null) {
                            value = "";
                        }

                        if (value !== "") {
                            await createAttributeRef.current({
                                ProjectId: realProjectId,
                                AttributeName: key,
                                Value: value,
                            });
                        }
                    }
                    await offlineStore.removeInspection(insp.offlineId);
                    console.info(
                        "[SyncManager] Inspection synced",
                        insp.offlineId,
                    );
                } catch (e: any) {
                    console.error("[SyncManager] Failed to sync inspection", e);
                    failures.push({
                        kind: "inspection",
                        id: insp.offlineId,
                        message: e?.message || "Inspection sync failed",
                        at: Date.now(),
                    });
                }
            }

            const remainingPhotos = await offlineStore.getOfflinePhotos();
            for (const photo of remainingPhotos) {
                try {
                    const mappedProjectId =
                        await offlineStore.getProjectMapping(photo.projectId);
                    const realProjectId =
                        idMap.get(photo.projectId) ||
                        mappedProjectId ||
                        photo.projectId;

                    if (String(realProjectId).startsWith("offline-")) {
                        if (pendingProjectIds.has(photo.projectId)) {
                            deferred.push({
                                kind: "photo",
                                id: photo.offlineId,
                                message: "Waiting for project sync",
                                at: Date.now(),
                            });
                        } else {
                            const message =
                                "Photo has no server project mapping";
                            console.warn("[SyncManager]", message, photo.offlineId);
                            failures.push({
                                kind: "photo",
                                id: photo.offlineId,
                                message,
                                at: Date.now(),
                            });
                        }
                        continue;
                    }

                    const tasks = await listTasksRef.current({
                        ProjectId: realProjectId,
                    });
                    const initialInspectionTask = (tasks || []).find(
                        (t: any) => t.Name === "Initial Inspection",
                    );
                    const realTaskId = initialInspectionTask?.Id;

                    if (!realTaskId) {
                        const message =
                            "Initial Inspection task not found after project create";
                        console.warn("[SyncManager]", message, realProjectId);
                        failures.push({
                            kind: "photo",
                            id: photo.offlineId,
                            message,
                            at: Date.now(),
                        });
                        continue;
                    }

                    const data = new FormData();
                    data.append("File", photo.file, photo.fileName);
                    data.append("Label", photo.label);
                    data.append("TaskId", realTaskId);
                    data.append("ProjectId", realProjectId);
                    if (photo.note) {
                        data.append("Note", photo.note);
                    }

                    await uploadAssetRef.current(data);
                    await offlineStore.removeOfflinePhoto(photo.offlineId);
                    console.info("[SyncManager] Photo synced", photo.offlineId);
                } catch (e: any) {
                    console.error("[SyncManager] Failed to sync photo", e);
                    failures.push({
                        kind: "photo",
                        id: photo.offlineId,
                        message: e?.message || "Photo sync failed",
                        at: Date.now(),
                    });
                }
            }

            await offlineStore.saveSyncFailures(failures);
            setLastFailures(failures);

            if (failures.length === 0 && deferred.length === 0) {
                const hadWork =
                    projects.length > 0 ||
                    inspections.length > 0 ||
                    offlinePhotos.length > 0;
                if (hadWork) {
                    showSuccessRef.current(
                        "Offline data successfully synced to server!",
                    );
                }
            } else if (failures.length > 0) {
                const detail = failures
                    .map((f) => `${f.kind}: ${f.message}`)
                    .join("; ");
                console.warn("[SyncManager] Sync finished with failures:", detail);
                showErrorRef.current(
                    `${failures.length} offline item(s) failed to sync. ${failures[0]?.message || ""}`,
                );
            } else if (opts?.manual && deferred.length > 0) {
                showErrorRef.current(
                    "Still waiting for project create to finish before inspection/photos can sync.",
                );
            }
        } catch (e) {
            console.error("[SyncManager] Unexpected sync error", e);
        } finally {
            syncInProgressRef.current = false;
            setIsSyncing(false);
        }
    }, []);

    // Auto-sync once when we come online (or on first online mount).
    useEffect(() => {
        if (!isOnline) {
            hasAttemptedSyncRef.current = false;
            return;
        }
        if (hasAttemptedSyncRef.current) return;
        hasAttemptedSyncRef.current = true;
        syncOfflineQueue();
    }, [isOnline, syncOfflineQueue]);

    // Only restore a stored failure banner if those items are still pending.
    useEffect(() => {
        (async () => {
            const [stored, counts] = await Promise.all([
                offlineStore.getSyncFailures(),
                offlineStore.getPendingCounts(),
            ]);
            const stillPending =
                counts.projects + counts.inspections + counts.photos > 0;
            if (!stillPending) {
                if (stored.length) {
                    await offlineStore.saveSyncFailures([]);
                }
                setLastFailures([]);
                return;
            }
            setLastFailures(stored);
        })().catch(() => {});
    }, []);

    if (!isSyncing && lastFailures.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 20,
                right: 20,
                bgcolor: isSyncing ? "primary.500" : "danger.600",
                color: "white",
                p: 2,
                borderRadius: "4px",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                zIndex: 9999,
                maxWidth: 360,
            }}
        >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                {isSyncing ? (
                    <RefreshCw className="animate-spin" size={18} />
                ) : null}
                <Typography sx={{ color: "white", flex: 1 }}>
                    {isSyncing
                        ? "Syncing offline changes..."
                        : `${lastFailures.length} item(s) failed to sync`}
                </Typography>
                {!isSyncing ? (
                    <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => dismissFailures()}
                        sx={{ minWidth: 0, p: 0.25, color: "white" }}
                        aria-label="Dismiss"
                    >
                        <X size={16} />
                    </Button>
                ) : null}
            </Box>
            {!isSyncing && lastFailures[0]?.message ? (
                <Typography level="body-xs" sx={{ color: "white", opacity: 0.9 }}>
                    {lastFailures[0].message}
                </Typography>
            ) : null}
            {!isSyncing && lastFailures.length > 0 ? (
                <Button
                    size="sm"
                    variant="solid"
                    color="neutral"
                    onClick={() => syncOfflineQueue({ manual: true })}
                    sx={{ borderRadius: "4px", alignSelf: "flex-start" }}
                >
                    Retry Sync
                </Button>
            ) : null}
        </Box>
    );
}
